// WIRE CONTRACTS FOR THE MAVEN RUN (CR-20260910-001, contract M1 v0.3).
//
// These mirror the FROZEN backend DTOs in
//   ets.platform/src/ETS.ShellService.Shell.Protocol/Operations/Maven/RunMavenSkill.cs
//   ets.platform/src/ETS.ShellService.Shell.Protocol/Dto/Maven/MavenResultReadyDto.cs
//   ets.platform/src/ETS.ShellService.Shell.Protocol/Dto/Maven/MavenRunStageDto.cs
// field for field. Field names are camelCase: the shell-router COMMAND path serializes with plain
// JSON.stringify (NO class-transformer) and the backend Newtonsoft deserializes case-insensitively
// into the PascalCase C# DTOs; the inbound ESS push arrives camelCase on the frontend. Do NOT add
// class-transformer decorators to anything in this file.
//
// DATE RULE (M1 section 2, ADR-037 / WIRE-01): every date crosses as a calendar STRING
// 'yyyy-MM-dd', NEVER a JS Date. A Date serialized at local midnight shifts a calendar day when
// parsed as an instant in another timezone. The mapping lives in maven-run-client.ts and builds
// the string from LOCAL date parts — never toISOString, which converts to UTC first.
//
// SEND: RunMavenSkill (command). There is NO cancel and NO stop operation, and that is deliberate
// (ruling R-D): pressing Apply again supersedes the run in flight, and supersede is the only way a
// run ends early. Do not add one.
// RECEIVE: MavenRunStageDto (several per run, not deliveries) and MavenResultReadyDto (exactly one
// per run), both over ESS, correlated by runId.

import {ProtocolCommand} from '../../service-model/protocol-command.interface';
import {SHELL_PROTOCOL_OPERATIONS_NS} from '../../shell-communication/operations/shell-protocol-namespace.const';
import {CashFlowAdjustment} from '../../adjustment-pricing-grid/model/CashFlowAdjustment';
import {HedgeStateTransaction} from '../../hedging-grid/data-model/hedge-state-transaction';
import {OpgCoPilotMarketOverrideDto, OpgCoPilotTradeDto} from '../../copilot-shared/opg-copilot-operations';

// Documentary, per M1 section 3. Nothing in backend production code compares it — only the
// contract does. Bump in lockstep with a contract change, never on its own.
export const MAVEN_CONTRACT_VERSION = 1;

// The two ESS message types the panel subscribes to (= the backend class names). Both were
// registered on all three routing sites by the foundation pass; nothing more is needed there.
export const MAVEN_RESULT_READY_MESSAGE_TYPE = 'MavenResultReadyDto';
export const MAVEN_RUN_STAGE_MESSAGE_TYPE = 'MavenRunStageDto';

// The closed set of run outcomes (MavenRunOutcomes). An outcome outside this set is a DEFECT
// (guarantee G12), never something to render optimistically.
export const MAVEN_OUTCOME_SUCCEEDED = 'succeeded';
export const MAVEN_OUTCOME_EMPTY = 'empty';
export const MAVEN_OUTCOME_SUPERSEDED = 'superseded';
export const MAVEN_OUTCOME_TERMINAL_FAILED = 'terminalFailed';
export const MAVEN_OUTCOME_PRE_SEND_BLOCKED = 'preSendBlocked';

// The stages, in the order a healthy run emits them (MavenRunStages). The day list is resolved
// FIRST (ruling: it is cheap and needs no prices), then the roll, the close and the hedges are
// ordered together.
// ⚠ 'candidateDays', NOT 'daysFound'. Renamed on the backend by ruling L33 while this lane was
// idle, and the drift is invisible at runtime: the client passes dto.stage through verbatim, so a
// stale constant does not throw or log — it simply never matches, and the first stage silently
// stops being recognised. Verified against MavenRunStages.CandidateDays on the merged M3.
export const MAVEN_STAGE_CANDIDATE_DAYS = 'candidateDays';
export const MAVEN_STAGE_ROLL = 'roll';
export const MAVEN_STAGE_HEDGES = 'hedges';
export const MAVEN_STAGE_CLOSE = 'close';
export const MAVEN_STAGE_PRICING = 'pricing';
export const MAVEN_STAGE_RANKING = 'ranking';

export const MAVEN_STAGE_ORDER: string[] = [
    MAVEN_STAGE_CANDIDATE_DAYS,
    MAVEN_STAGE_ROLL,
    MAVEN_STAGE_HEDGES,
    MAVEN_STAGE_CLOSE,
    MAVEN_STAGE_PRICING,
    MAVEN_STAGE_RANKING
];

// THE CLOSED REFUSAL LIST (MavenRefusals), contract M1 section 3. A consumer meeting a code
// outside this list treats it as a DEFECT (G12) — it does not guess, and it does not swallow.
// The CODE is what crosses the wire; the WORDING lives on the client (ruling Q-M5) and is in
// maven-refusal-messages.ts.
export const MAVEN_REFUSAL_MISSING_PORTFOLIO = 'MissingPortfolio';
export const MAVEN_REFUSAL_MISSING_TEMPLATE = 'MissingTemplate';
export const MAVEN_REFUSAL_TEMPLATE_NOT_FOUND = 'TemplateNotFound';
export const MAVEN_REFUSAL_NO_SAVED_POSITIONS = 'NoSavedPositions';
export const MAVEN_REFUSAL_MISSING_DATE = 'MissingDate';
export const MAVEN_REFUSAL_RANGE_EXCEEDS_CAP = 'RangeExceedsCap';
export const MAVEN_REFUSAL_NO_HEDGE_SIDE = 'NoHedgeSide';
export const MAVEN_REFUSAL_MALFORMED_SIDE_INPUTS = 'MalformedSideInputs';
export const MAVEN_REFUSAL_EXPECTED_RANGE_UNAVAILABLE = 'ExpectedRangeUnavailable';
export const MAVEN_REFUSAL_NO_ZONE = 'NoZone';
export const MAVEN_REFUSAL_END_STRIKE_INSIDE_START = 'EndStrikeInsideStart';
// CR-20260915-001: a market-override record malformed or incomplete for the sides requested.
export const MAVEN_REFUSAL_MALFORMED_MARKET_OVERRIDES = 'MalformedMarketOverrides';
// CR-20260916-001: a saved position leg past 16:20 ET on its expiration date — cannot be priced.
export const MAVEN_REFUSAL_EXPIRED_POSITIONS = 'ExpiredPositions';
// CR-20260916-001: ExistingHedges = close and a hedge leg past its expiration.
export const MAVEN_REFUSAL_EXPIRED_HEDGES = 'ExpiredHedges';
// CR-20260916-003: ExistingHedges = close with no expiration or no hedge selected.
export const MAVEN_REFUSAL_CLOSE_SELECTION_EMPTY = 'CloseSelectionEmpty';
// CR-20260916-003: a selected hedge id is not on the owner's saved book.
export const MAVEN_REFUSAL_UNKNOWN_CLOSE_HEDGE_ID = 'UnknownCloseHedgeId';

export const MAVEN_REFUSALS: string[] = [
    MAVEN_REFUSAL_MISSING_PORTFOLIO,
    MAVEN_REFUSAL_MISSING_TEMPLATE,
    MAVEN_REFUSAL_TEMPLATE_NOT_FOUND,
    MAVEN_REFUSAL_NO_SAVED_POSITIONS,
    MAVEN_REFUSAL_MISSING_DATE,
    MAVEN_REFUSAL_RANGE_EXCEEDS_CAP,
    MAVEN_REFUSAL_NO_HEDGE_SIDE,
    MAVEN_REFUSAL_MALFORMED_SIDE_INPUTS,
    MAVEN_REFUSAL_EXPECTED_RANGE_UNAVAILABLE,
    MAVEN_REFUSAL_NO_ZONE,
    MAVEN_REFUSAL_END_STRIKE_INSIDE_START,
    MAVEN_REFUSAL_MALFORMED_MARKET_OVERRIDES,
    MAVEN_REFUSAL_EXPIRED_POSITIONS,
    MAVEN_REFUSAL_EXPIRED_HEDGES,
    MAVEN_REFUSAL_CLOSE_SELECTION_EMPTY,
    MAVEN_REFUSAL_UNKNOWN_CLOSE_HEDGE_ID
];

// ---- Request-side DTOs (FE -> BE, on RunMavenSkill) ----------------------------------------

// S8. Three kinds only: "range" | "followThrough" | "expectedRange". "Both" is NOT offered
// (ruling Q-M2). Every kind starts from that side's anchor plus its offset — the client never
// enters a start, so there is no start field here and adding one would be a contract change.
export interface MavenCoverageDto {
    kind: string;
    // "range" ONLY: the END strike the trader entered. Maven computes the distance the hedge
    // engine actually wants from this end strike and the start, and never sends the end strike on
    // to the engine.
    endStrike: number | null;
    // Additive fields: absent rangeMode means the legacy End Strike workflow.
    rangeMode?: string | null;
    rangeSize?: number | null;
}

// S9. Kind-discriminated, exactly one of the two populated, as the ORION (OPG) questionnaire
// asks it (ruling Q-M1): "dollar" with an amount, or "relative" with one of the FOUR levels
// Low | Moderate | Aggressive | All. Never High, never Conservative, never Auto.
export interface MavenMaxRiskDto {
    kind: string;
    amount: number | null;
    level: string | null;
}

// S5-S9, one side. Per side its OWN offset, coverage, minimum profit and max risk; everything
// else is shared. At least one of calls/puts must be non-null or the run is refused NoHedgeSide.
export interface MavenSideInputsDto {
    mode?: 'custom' | 'preset';
    presetId?: string;
    // S7, in POINTS, per side: the CALL offset is measured from the short CALL and the PUT offset
    // from the short PUT of the paired adjustment solution's after-state.
    offsetFromShorts: number;
    coverage: MavenCoverageDto;
    // S6, PLAIN DOLLARS, greater than zero. A normalized entry is converted with the portfolio's
    // default quantity HERE ON THE FRONTEND before it crosses, exactly as the ORION (OPG) does.
    minProfitDollars: number;
    maxRisk: MavenMaxRiskDto;
}

// The order form. The panel sends its Setup BY IDS: portfolio, portfolio OWNER and template.
//
// NOT ON THIS ORDER, deliberately (M1 section 3 and section 8, confirmed as A5): the saved
// positions, the saved hedge book, the template JSON, the expected-range model, directional bias
// (S10 is a visible placeholder and reaches nothing), any panel context, any score or fit input.
// All of it is read on the BACKEND by id, for the PORTFOLIO OWNER rather than the person logged
// in, because a super user may run on another trader's portfolio.
export class RunMavenSkill implements ProtocolCommand {
    constructor(
        public readonly contractVersion: number,
        public readonly runId: string,
        public readonly panelInstanceId: string,
        public readonly portfolioId: string,
        public readonly portfolioOwnerId: string,
        public readonly templateId: string,
        public readonly dateKind: string,
        public readonly date: string | null,
        public readonly rangeStart: string | null,
        public readonly rangeEnd: string | null,
        public readonly weekdayExclusions: string[],
        public readonly specificDateExclusions: string[],
        // Days brought back from the weekday rule - any excluded weekday since CR-20260915-002
        // (was mondayOverrides). Mirrors RunMavenSkill.WeekdayOverrides.
        public readonly weekdayOverrides: string[],
        public readonly existingHedges: string,
        // CR-20260916-003: under "close", the toggled expirations ('yyyy-MM-dd') and the chosen
        // hedges by saved-book group id. Mirrors RunMavenSkill.CloseExpirations / CloseHedgeIds.
        // EMPTY under "ignore" — the pre-CR request plus two empty arrays.
        public readonly closeExpirations: string[],
        public readonly closeHedgeIds: string[],
        public readonly calls: MavenSideInputsDto | null,
        public readonly puts: MavenSideInputsDto | null,
        // CR-20260915-001. The trader's per-date market overrides, the ORION (OPG)'s own wire
        // record. EDITED and COMPLETE dates only; null = measure everything, byte-for-byte the
        // pre-CR request. Maven hands the array to each side's ORION (OPG) hedge request
        // verbatim; the APG ORION request does not carry it (owner ruling 2026-09-15).
        public readonly marketOverrides: OpgCoPilotMarketOverrideDto[] | null
    ) {}

    getOperationName(): string {
        return SHELL_PROTOCOL_OPERATIONS_NS + '.RunMavenSkill';
    }
}


// THE PANEL LEASE DECLARATION (owner ruling 2026-09-14, option 2). The panel tells the backend
// "I am open, this is my date range" so the backend can hold the run's skew slots warm ahead of
// time. The panel holds nothing itself; the backend owns the lease. An empty date kind releases
// it, which is what the panel sends on destroy.
//
// KEYED BY THE DATE RANGE: the backend recomputes the held slots whenever this changes, and an
// incomplete range (a range missing an end) holds none. No portfolio travels here — iteration 1
// warms SPX, so a portfolio switch does not disturb the lease.
export class WarmMavenSlots implements ProtocolCommand {
    constructor(
        public readonly contractVersion: number,
        public readonly panelInstanceId: string,
        public readonly dateKind: string,
        public readonly date: string | null,
        public readonly rangeStart: string | null,
        public readonly rangeEnd: string | null
    ) {}

    getOperationName(): string {
        return SHELL_PROTOCOL_OPERATIONS_NS + '.WarmMavenSlots';
    }
}

// ---- Receive-side DTOs (BE -> FE, ESS push; camelCase as observed on the frontend) ----------

// Several per run, and NOT deliveries (guarantee G6): they say what the run is doing, and the
// panel shows them while it waits. One delivery ends the run.
export interface MavenRunStageDto {
    topic: string;
    runId: string;
    stage: string;
    // ⚠ NEVER A COUNT OF DAYS (leader ruling L28). Contract M1 section 6.1 once offered "the
    // number of days found" as the example, and that one example broke the rule beside it: a rail
    // reading "5 days found" next to three cards invites exactly the reconciliation R-C exists to
    // prevent — the trader counting what he asked for against what came back. Withdrawn.
    detail: string | null;
}

// The adjustment half of a card. The adjustments cross VERBATIM as the APG ORION delivered them
// and are handed to the adjustment pop-up untouched — Maven maps nothing on that path.
export interface MavenAdjustmentDto {
    adjustments: CashFlowAdjustment[];
    adjustmentTypes: string[];
    shortStrikes: MavenShortStrikesDto | null;
    atm: number;
    reason: string | null;
    // The strategy codes the card re-prices through, live. These are the SAME codes the run
    // priced on: the adjustment figure is the option strategy pricing service's price for them,
    // taken exactly as delivered.
    //
    // ⚠ AN ARRAY, NOT ONE STRING (leader rulings L13 / L30). A COUPLE'S CELL HOLDS TWO STRATEGIES
    // AND THEREFORE TWO CODES. A single code would live-re-price a coupled adjustment on HALF of
    // itself: the card's total would drift from the delivered price on every tick, and it would
    // read as a pricing bug rather than as the truncation it is. Subscribe to all of them and sum.
    strategyCodes: string[];
}

export interface MavenShortStrikesDto {
    call: number | null;
    put: number | null;
}

// The hedge half of a card. Calls/Puts are the ORION (OPG)'s own trade shape VERBATIM — Maven
// takes what the supplier ranked first and never re-ranks or audits it (guarantee G16).
// `transaction` is built on the BACKEND in the shape the hedge pop-up consumes: the close of the
// old hedges (when the trader chose Close) and the opening of the new ones, in ONE transaction
// list. The two orders of a package are never merged (guarantee G11) — the adjustment stays
// separate.
export interface MavenHedgesDto {
    calls: OpgCoPilotTradeDto | null;
    puts: OpgCoPilotTradeDto | null;
    close: MavenCloseSolutionDto | null;
    transaction: HedgeStateTransaction[];
}

export interface MavenCloseSolutionDto {
    targetHedgeId: string;
    title: string;
    legs: MavenCloseLegDto[];
    prices: MavenClosePricesDto;
}

export interface MavenCloseLegDto {
    quantity: number;
    ticker: string;
    displayName: string;
    action: string;
    side: string;
    effect: string;
    groupId: string;
    asset: string;
    optionType: string;
    strike: number | null;
    expiry: string | null;
    hedgeLabel: string | null;
    hedgeColour: string | null;
}

// ALL-OR-NOTHING: a single missing mid leaves every figure null. Never zero (guarantee G4) — a
// package that cannot be priced produces no card at all.
export interface MavenClosePricesDto {
    net: number | null;
    orderPrice: number | null;
    ledgerPrice: number | null;
}

// The five figures. On `prices` they are NORMALIZED to the portfolio's default quantity; on
// `pricesRaw` they are the same five raw (x default quantity x 100). Signed: credit positive,
// debit negative. All of them come from the run's ONE acquisition (guarantee G3), and no time
// stamp travels.
export interface MavenPricesDto {
    adjustment: number | null;
    hedges: number | null;
    closeOut: number | null;
    total: number | null;
    perDay: number | null;
}

// Per side, the STRUCTURE the hedge engine actually delivered — "Vertical", "Condor". Not a
// coverage kind: the engine does not report one on a single-family run, so the card and the
// Summary name what came back rather than what was asked for.
export interface MavenCoverageDeliveredDto {
    calls: string | null;
    puts: string | null;
}

// The calendar verdict for the card's OWN date, attached as DATA (CR-20260921-001: one entry, the
// ORION's verdict as delivered; the neighbour window is gone). The frontend composes the wording.
// Under a historical Quote Generator cursor the day is 'unknown' — the calendar's coverage starts
// today. The field keeps its name so the wire shape did not move.
export interface MavenNearbyReportDto {
    date: string;
    tier: string;
    events: string[];
}

export interface MavenCardDto {
    // The RUN's rank, 1..N. It stays on the card through a manual re-sort, and a live tick NEVER
    // re-orders the cards (A3, guarantee G8).
    rank: number;
    date: string;
    dte: number;
    // The card's identity within its date: the ORION's ranks of its hedge pair, "c1p2" / "c1" /
    // "p2" (CR-20260921-003; was "primary" | "secondHedge" | "mvp2"). Unique per date.
    variant: string;
    adjustment: MavenAdjustmentDto;
    hedges: MavenHedgesDto;
    prices: MavenPricesDto;
    pricesRaw: MavenPricesDto;
    // Supplier codes the card itself renders. Maven adds NONE, and no dropped-, excluded-,
    // filtered- or unpriced-day reason travels here (ruling R-C) — that is logged on the backend.
    notes: string[];
    coverageDelivered: MavenCoverageDeliveredDto | null;
    nearbyReports: MavenNearbyReportDto[];
    // Every leg's ticker, for the panel's live subscription. The adjustment re-prices through its
    // strategy code instead.
    tickers: string[];
    // Always null in iteration 1. The card renders an explicit non-value, never a number.
    score: number | null;
}

// EXACTLY ONE per run (guarantee G6), when the run completes or fails.
//
// `cards` carries EVERY package the run produced — never a capped set. The "show top N" setting
// is a DISPLAY limit the panel applies when it renders (ruling R-B); it is not a cut on the
// delivery. A succeeded run carries two or more cards, never one (guarantee G1).
//
// No excluded-days list and no run-level reason codes travel (ruling R-C). `failureDetail`
// carries the message for a failure or a refusal, and the ONE sentence an 'empty' run shows.
export interface MavenResultReadyDto {
    topic: string;
    runId: string;
    outcome: string;
    failureDetail: string | null;
    // The portfolio's default quantity the normalized figures refer to.
    defaultQty: number;

    /**
     * "single" | "double" — the portfolio fact BOTH order pop-ups need (ruling L39).
     *
     * ⚠ THIS FIELD WAS ON THE WIRE AND MISSING FROM THIS MIRROR, and the Details column's two order
     * controls were dead because of it. `MavenResultReadyDto.StrategyType` is ProtoMember(7) on the
     * backend and the run log ends `strategyType=double`; the value arrived on every run and nothing
     * here carried it.
     *
     * DELIVERED, NEVER INFERRED, and never defaulted. Both ways of getting it wrong are silent: the
     * hedge pop-up filters legs out of the transaction inside itself, and the adjustment writes its
     * after-state into the wrong slot. Neither raises anything. When it is absent the panel disables
     * the controls and says so — an absent strategy type must not become a guessed one.
     */
    strategyType: string;

    cards: MavenCardDto[];

    // CR-20260914-001 — THE SCOPED EXCEPTION TO R-C (owner ruling 2026-09-14). One line per (date,
    // side) on which the hedge could not be built to the EXPECTED RANGE: the day's band ended at or
    // short of the hedge start. Worded on the backend, date order. Travels on 'succeeded' AND on
    // 'empty'. Null or empty = nothing to say. This is the ONE cause of a dropped day that is
    // shown; R-C stands for every other.
    expectedRangeNotes: string[] | null;
}
